import type { PullRequestReviewThread, PullRequestTimelineComment } from "@afar-research/contracts";
import {
  ChevronDownIcon,
  GitCommitIcon,
  GitPullRequestIcon,
  MessageSquareIcon,
  TagIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "../../lib/utils";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import type { PullRequestSurfaceDescriptor } from "../../pullRequestSurfaceStore";
import { PullRequestActorAvatar } from "./pullRequestPresentation";
import { PullRequestReactionBar } from "./PullRequestReactionBar";
import {
  conversationActors,
  groupPullRequestTimelineConversations,
  type PullRequestTimelineEntry,
} from "./pullRequestTimeline.logic";

/**
 * A pull request's history on one rail, following T3Code: every entry hangs off a single line,
 * marked by whoever did it. A comment is a card because it is something someone said; a push or
 * a label is a line, because it is something that happened.
 */

function Marker({ children }: { readonly children: ReactNode }) {
  return (
    <span className="absolute -left-[1.55rem] top-0 z-10 flex size-7 items-center justify-center bg-background">
      {children}
    </span>
  );
}

function IconMarker({ icon }: { readonly icon: ReactNode }) {
  return (
    <Marker>
      <span className="flex size-5 items-center justify-center text-muted-foreground">{icon}</span>
    </Marker>
  );
}

function ActorMarker({
  actor,
  fallback,
}: {
  readonly actor: PullRequestTimelineComment["author"];
  readonly fallback: ReactNode;
}) {
  return actor === null ? (
    <IconMarker icon={fallback} />
  ) : (
    <Marker>
      <PullRequestActorAvatar actor={actor} className="size-5 bg-muted text-[9px]" />
    </Marker>
  );
}

function When({ at }: { readonly at: string }) {
  return (
    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/70">
      {formatRelativeTimeLabel(at)}
    </span>
  );
}

function Row({ children }: { readonly children: ReactNode }) {
  return <div className="relative pb-5 pl-1">{children}</div>;
}

function CommentCard({
  comment,
  reacting,
  editing,
}: {
  readonly comment: PullRequestTimelineComment;
  readonly reacting: ReactionTarget | null;
  readonly editing: CommentEditing | null;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const canEdit = editing !== null && comment.viewerDidAuthor;
  return (
    <div className="group/remark-card rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
        <span className="min-w-0 truncate font-medium">
          {comment.author?.login ?? "Unknown author"}
        </span>
        <span className="ml-auto flex items-center gap-2">
          {comment.viewerDidAuthor ? (
            <span className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground">
              You
            </span>
          ) : null}
          <When at={comment.createdAt} />
        </span>
      </div>
      {/* `group/remark` is what reveals the add-a-reaction button on hover. */}
      <div className="group/remark px-3 py-2">
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{comment.body}</p>
        {reacting === null ? null : (
          <PullRequestReactionBar
            className="mt-2"
            canReact={reacting.canReact}
            descriptor={reacting.descriptor}
            projectRevision={reacting.projectRevision}
            reactions={comment.reactions}
            resourceVersion={reacting.resourceVersion}
            subjectId={comment.id}
          />
        )}
      </div>
    </div>
  );
}

function Comment({
  comment,
  reacting,
  editing,
}: {
  readonly comment: PullRequestTimelineComment;
  readonly reacting: ReactionTarget | null;
  readonly editing: CommentEditing | null;
}) {
  return (
    <Row>
      <ActorMarker actor={comment.author} fallback={<MessageSquareIcon className="size-3.5" />} />
      <CommentCard comment={comment} editing={editing} reacting={reacting} />
    </Row>
  );
}

/** Overlapping faces for the people who spoke, so a conversation is marked by who held it. */
function ActorsMarker({
  actors,
  muted,
}: {
  readonly actors: ReadonlyArray<NonNullable<PullRequestTimelineComment["author"]>>;
  readonly muted: boolean;
}) {
  if (actors.length === 0) return <IconMarker icon={<MessageSquareIcon className="size-3.5" />} />;
  return (
    <Marker>
      <span className={cn("flex items-center -space-x-1.5", muted && "opacity-60")}>
        {actors.slice(0, 3).map((actor) => (
          <PullRequestActorAvatar
            actor={actor}
            className="size-5 bg-muted text-[9px] ring-2 ring-background"
            key={actor.login}
          />
        ))}
      </span>
    </Marker>
  );
}

function Conversation({
  comments,
  at,
  reacting,
  editing,
}: {
  readonly comments: ReadonlyArray<PullRequestTimelineComment>;
  readonly at: string;
  readonly reacting: ReactionTarget | null;
  readonly editing: CommentEditing | null;
}) {
  const [open, setOpen] = useState(false);
  const actors = conversationActors(comments);
  // A lone remark is not a conversation to fold; it reads as itself.
  if (comments.length === 1 && comments[0] !== undefined) {
    return <Comment comment={comments[0]} editing={editing} reacting={reacting} />;
  }
  return (
    <Row>
      <ActorsMarker actors={actors} muted={!open} />
      <button
        className={cn(
          "flex w-full min-w-0 items-center gap-3 py-1 text-left transition-opacity",
          open ? "text-foreground" : "text-muted-foreground opacity-70 hover:opacity-100",
        )}
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-medium">{comments.length} comments</span>
          <span className="block truncate text-[10px] text-muted-foreground">
            {actors.length} {actors.length === 1 ? "author" : "authors"} ·{" "}
            {formatRelativeTimeLabel(at)}
          </span>
        </span>
        <ChevronDownIcon
          aria-hidden
          className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>
      {open ? (
        <div className="mt-2 grid gap-3">
          {comments.map((comment) => (
            <CommentCard comment={comment} editing={editing} key={comment.id} reacting={reacting} />
          ))}
        </div>
      ) : null}
    </Row>
  );
}

/** What rewriting a remark needs, where the viewer is allowed to rewrite one at all. */
export interface CommentEditing {
  readonly onSave: (comment: PullRequestTimelineComment, body: string) => void;
}

/** What a reaction needs to name its subject, carried together because it always travels so. */
export interface ReactionTarget {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly projectRevision: string | undefined;
  readonly resourceVersion: string;
  readonly canReact: boolean;
}

export function PullRequestTimelineRail({
  entries,
  reviewThreads,
  reacting = null,
  editing = null,
  order = "newest",
}: {
  readonly entries: ReadonlyArray<PullRequestTimelineEntry>;
  readonly reviewThreads: ReadonlyArray<PullRequestReviewThread>;
  /** Absent where the pull request's own detail is not to hand, which is where reacting needs it. */
  readonly reacting?: ReactionTarget | null;
  /** Absent where the viewer may not rewrite a remark, or the Environment cannot take one. */
  readonly editing?: CommentEditing | null;
  /**
   * Following T3Code, newest first by default: what happened last is what a reader opening the
   * tab is asking about — whether it merged, what the last review said.
   */
  readonly order?: "newest" | "oldest";
}) {
  const rows = groupPullRequestTimelineConversations(
    order === "newest" ? [...entries].toReversed() : entries,
  );
  if (entries.length === 0 && reviewThreads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
        <GitPullRequestIcon aria-hidden className="mb-2 size-5" />
        <p className="text-xs">No activity yet.</p>
      </div>
    );
  }
  return (
    <div className="px-4 py-5">
      <div className="relative mx-auto ml-7 max-w-3xl pl-5">
        <span aria-hidden className="absolute bottom-5 left-0 top-1 w-px bg-border/45" />
        {rows.map((row) => {
          if (row.kind === "conversation") {
            return (
              <Conversation
                at={row.at}
                comments={row.comments}
                editing={editing}
                key={`conversation:${row.comments[0]?.id ?? row.at}`}
                reacting={reacting}
              />
            );
          }
          const entry = row.entry;
          if (entry.kind === "commits") {
            const last = entry.commits.at(-1);
            return (
              <Row key={`commits:${last?.oid ?? entry.at}`}>
                <ActorMarker
                  actor={last?.author ?? null}
                  fallback={<GitCommitIcon className="size-3.5" />}
                />
                <div className="flex items-baseline gap-2 text-xs">
                  <span className="min-w-0 flex-1">
                    {/* The run is one push to the reader; its commits are what it carried. */}
                    {entry.commits.length === 1
                      ? "pushed a commit"
                      : `pushed ${entry.commits.length} commits`}
                  </span>
                  <When at={entry.at} />
                </div>
                <ul className="mt-1.5 grid gap-1">
                  {entry.commits.map((commit) => (
                    <li className="flex items-start gap-2 text-xs" key={commit.oid}>
                      <GitCommitIcon
                        aria-hidden
                        className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/70"
                      />
                      <a
                        className="min-w-0 flex-1 hover:underline"
                        href={commit.url}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <span className="wrap-anywhere">{commit.messageHeadline}</span>{" "}
                        <span className="font-mono text-muted-foreground">
                          {commit.oid.slice(0, 7)}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </Row>
            );
          }
          return (
            <Row key={entry.activity.id}>
              <ActorMarker
                actor={entry.activity.actor}
                fallback={<TagIcon className="size-3.5" />}
              />
              <div className="flex items-baseline gap-2 text-xs">
                <span className="min-w-0 flex-1 wrap-anywhere text-muted-foreground">
                  {entry.activity.summary}
                </span>
                <When at={entry.at} />
              </div>
            </Row>
          );
        })}
      </div>

      {reviewThreads.length === 0 ? null : (
        <section aria-label="Review threads" className="mt-2">
          <p className="text-xs font-medium text-muted-foreground">
            Review threads ({reviewThreads.length})
          </p>
          <ul className="mt-2 grid gap-2">
            {reviewThreads.map((thread) => (
              <li className="rounded-md border border-border px-3 py-2" key={thread.id}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="min-w-0 flex-1 truncate font-mono">
                    {thread.path}
                    {thread.line === null ? "" : `:${thread.line}`}
                  </span>
                  {/* Resolved and outdated are different things, and a reader acts on each
                      differently, so a thread that is both says both. */}
                  <span className="flex shrink-0 items-center gap-1 text-[10px]">
                    {thread.isOutdated ? (
                      <span className="rounded-full border border-border px-1.5 text-muted-foreground">
                        Outdated
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        "rounded-full border px-1.5",
                        thread.isResolved
                          ? "border-border text-muted-foreground"
                          : "border-amber-600/40 text-amber-600 dark:text-amber-400/90",
                      )}
                    >
                      {thread.isResolved ? "Resolved" : "Unresolved"}
                    </span>
                  </span>
                </div>
                <ul className="mt-1.5 grid gap-1">
                  {thread.comments.map((comment) => (
                    <li className="group/remark text-xs text-muted-foreground" key={comment.id}>
                      <span className="font-medium text-foreground">
                        {comment.author?.login ?? "Unknown author"}
                      </span>{" "}
                      <span className="wrap-anywhere">{comment.body}</span>
                      {reacting === null ? null : (
                        <PullRequestReactionBar
                          className="mt-1"
                          canReact={reacting.canReact}
                          descriptor={reacting.descriptor}
                          projectRevision={reacting.projectRevision}
                          reactions={comment.reactions}
                          resourceVersion={reacting.resourceVersion}
                          subjectId={comment.id}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
